import axiosInstance from '@/core/api'
import type {
	TransactionTypeStatus,
	TransactionSchedule,
	TransactionScheduleListResponse,
	CreateTransactionScheduleDto,
	UpdateTransactionScheduleDto,
	SetTemporaryDisableDto,
	SetTemporaryDisableResponse,
	ClearTemporaryDisableResponse,
	TransactionType,
} from '../../types'

class TransactionSettingsService {
	// ==================== Deposit ====================

	async getDepositStatus(): Promise<TransactionTypeStatus> {
		const response = await axiosInstance.get(
			'/admin/settings/transaction/deposit/status',
		)
		return response.data
	}

	async getDepositSchedules(): Promise<TransactionScheduleListResponse> {
		const response = await axiosInstance.get(
			'/admin/settings/transaction/deposit/schedules',
		)
		return response.data
	}

	async createDepositSchedule(
		data: CreateTransactionScheduleDto,
	): Promise<TransactionSchedule> {
		const response = await axiosInstance.post(
			'/admin/settings/transaction/deposit/schedules',
			data,
		)
		return response.data
	}

	async setDepositTemporaryDisable(
		data: SetTemporaryDisableDto,
	): Promise<SetTemporaryDisableResponse> {
		const response = await axiosInstance.post(
			'/admin/settings/transaction/deposit/temporary-disable',
			data,
		)
		return response.data
	}

	async clearDepositTemporaryDisable(): Promise<ClearTemporaryDisableResponse> {
		const response = await axiosInstance.delete(
			'/admin/settings/transaction/deposit/temporary-disable',
		)
		return response.data
	}

	// ==================== Withdraw ====================

	async getWithdrawStatus(): Promise<TransactionTypeStatus> {
		const response = await axiosInstance.get(
			'/admin/settings/transaction/withdraw/status',
		)
		return response.data
	}

	async getWithdrawSchedules(): Promise<TransactionScheduleListResponse> {
		const response = await axiosInstance.get(
			'/admin/settings/transaction/withdraw/schedules',
		)
		return response.data
	}

	async createWithdrawSchedule(
		data: CreateTransactionScheduleDto,
	): Promise<TransactionSchedule> {
		const response = await axiosInstance.post(
			'/admin/settings/transaction/withdraw/schedules',
			data,
		)
		return response.data
	}

	async setWithdrawTemporaryDisable(
		data: SetTemporaryDisableDto,
	): Promise<SetTemporaryDisableResponse> {
		const response = await axiosInstance.post(
			'/admin/settings/transaction/withdraw/temporary-disable',
			data,
		)
		return response.data
	}

	async clearWithdrawTemporaryDisable(): Promise<ClearTemporaryDisableResponse> {
		const response = await axiosInstance.delete(
			'/admin/settings/transaction/withdraw/temporary-disable',
		)
		return response.data
	}

	// ==================== Shared ====================

	async getScheduleById(id: string): Promise<TransactionSchedule> {
		// Try deposit first, then withdraw
		try {
			const response = await axiosInstance.get(
				`/admin/settings/transaction/deposit/schedules/${id}`,
			)
			return response.data
		} catch {
			const response = await axiosInstance.get(
				`/admin/settings/transaction/withdraw/schedules/${id}`,
			)
			return response.data
		}
	}

	async updateSchedule(
		id: string,
		data: UpdateTransactionScheduleDto,
		type: TransactionType,
	): Promise<TransactionSchedule> {
		const response = await axiosInstance.patch(
			`/admin/settings/transaction/${type}/schedules/${id}`,
			data,
		)
		return response.data
	}

	async deleteSchedule(
		id: string,
		type: TransactionType,
	): Promise<TransactionSchedule> {
		const response = await axiosInstance.delete(
			`/admin/settings/transaction/${type}/schedules/${id}`,
		)
		return response.data
	}

	// Generic methods based on type
	async getStatus(type: TransactionType): Promise<TransactionTypeStatus> {
		return type === 'deposit'
			? this.getDepositStatus()
			: this.getWithdrawStatus()
	}

	async getSchedules(
		type: TransactionType,
	): Promise<TransactionScheduleListResponse> {
		return type === 'deposit'
			? this.getDepositSchedules()
			: this.getWithdrawSchedules()
	}

	async createSchedule(
		type: TransactionType,
		data: CreateTransactionScheduleDto,
	): Promise<TransactionSchedule> {
		return type === 'deposit'
			? this.createDepositSchedule(data)
			: this.createWithdrawSchedule(data)
	}

	async setTemporaryDisable(
		type: TransactionType,
		data: SetTemporaryDisableDto,
	): Promise<SetTemporaryDisableResponse> {
		return type === 'deposit'
			? this.setDepositTemporaryDisable(data)
			: this.setWithdrawTemporaryDisable(data)
	}

	async clearTemporaryDisable(
		type: TransactionType,
	): Promise<ClearTemporaryDisableResponse> {
		return type === 'deposit'
			? this.clearDepositTemporaryDisable()
			: this.clearWithdrawTemporaryDisable()
	}
}

export const transactionSettingsService = new TransactionSettingsService()
